// frontend/src/features/onboarding/types.ts
import type { DeviceType } from '@/types/devices';

/**
 * Answer to the "Do you dose?" question on the capability step (Task 17).
 * `manual` = doses by hand, `doser` = has a dosing device, `no` = does not dose.
 */
export type DosingAnswer = 'manual' | 'doser' | 'no';

/**
 * State collected by the `/welcome` onboarding wizard.
 *
 * Shaped so the later step tasks slot in without reshaping:
 *   - Task 16 (CreateTankStep) drives `currentStep`.
 *   - Task 17 (CapabilityStep) writes `selectedBrands` and `dosing`.
 *   - Task 18 (branch step) reads `selectedBrands` to pick the device-connect
 *     vs first-test path.
 */
export interface OnboardingWizardState {
  /** Current step index into ONBOARDING_STEPS. */
  currentStep: number;
  /** Device brands the user said run on their tank (capability step). */
  selectedBrands: DeviceType[];
  /** Whether/how the user doses; `null` until answered. */
  dosing: DosingAnswer | null;
  /**
   * True once the user has connected a device in the first-value step. Drives
   * the FinishStep copy (mention the dashboard filling in) — a 30-day refresh
   * was kicked off, so data streams in over the next minutes.
   */
  connectedDevice: boolean;
}

/**
 * Identifier for a wizard step. A closed union (not `string`) so `renderStep`
 * can switch exhaustively — a typo or a newly added step id becomes a compile
 * error instead of silently falling through to a default.
 */
export type OnboardingStepId = 'create-tank' | 'capability' | 'first-value' | 'finish';

/**
 * A single wizard step definition (drives the progress indicator).
 */
export interface OnboardingStep {
  /** Unique step identifier. */
  id: OnboardingStepId;
  /** Short label for the progress indicator. */
  label: string;
}

/**
 * Ordered wizard steps.
 *
 * `first-value` is a single logical slot; Task 18 branches its content between
 * a device-connect step and a first-test step based on `selectedBrands`, but it
 * occupies one position in the progress indicator either way.
 */
export const ONBOARDING_STEPS: OnboardingStep[] = [
  { id: 'create-tank', label: 'Create tank' },
  { id: 'capability', label: 'Your setup' },
  { id: 'first-value', label: 'First data point' },
  { id: 'finish', label: 'Done' },
];

/**
 * Initial wizard state.
 */
export const INITIAL_ONBOARDING_STATE: OnboardingWizardState = {
  currentStep: 0,
  selectedBrands: [],
  dosing: null,
  connectedDevice: false,
};
